import { Flex, Grid, Group, Stack, Text } from '@mantine/core';
import { utils, writeFile } from 'xlsx';
import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import '../../scss/vendors/bstyles.scss';
import Button from '../../components/elements/Button';
import { useMutation } from '@tanstack/react-query';
import {
  getChartPlanCompletion,
  requestDownloadDataChartPlanTraining
} from '../../services/dashboardExecutive';
import { showNotification } from '../../utilities/mantineNotification';
import Card from '../../components/elements/Card';
import dayjs from 'dayjs';
import id from 'dayjs/locale/id';
import { dateFormat } from '../../utilities/dataFormat';
import * as Unicons from '@iconscout/react-unicons';
import ChartFilter from './ChartFilter';
import { useDisclosure } from '@mantine/hooks';
import ModalChartTraining from '../../components/fragments/ModalChart';
import BarChart from '../../components/elements/BarChart';
import LoaderChart from './LoaderChart';
import { HiChevronRight } from 'react-icons/hi';
import ModalDownload from '../dashboard_bcld/ModalDownload';
import useLocalStorage from '../../hooks/useUserData';

ChartJS.register(
  ChartDataLabels,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export interface DataDownloadReportTraining {
  courseId: string;
  courseName: string;
  completionDate: string;
  enrolmentDate: string;
  enrolmentMethod: string;
  buName: null | string;
  subBuName: null | string;
  deptName: null | string;
  binusianId: null | string;
  name: null | string;
  buCode: null | string;
  subBuCode: null | string;
  deptCode: null | string;
  sectionCode: null | string;
  sectionName: null | string;
  subSectionCode: null | string;
  subSectionName: null | string;
}

export interface ChartTrainingProps {
  startDate: string;
  endDate: string;
  title: string;
  businessUnit?: string;
  subBusinessUnit?: string;
  buCode?: null | string;
  buName?: null | string;
  subBuCode?: null | string;
  subBuName?: null | string;
  deptCode?: null | string;
  deptName?: null | string;
  sectionCode?: null | string;
  sectionName?: null | string;
  bandName?: string;
}

export const authorizedFilterBu = ['Director', 'General Manager'];
export const authorizedFilterSubBu = [
  'Director',
  'General Manager'
  // 'Senior Manager'
];
export const authorizedFilterDept = [
  'Director',
  'General Manager',
  'Senior Manager'
  // 'Manager'
];
export const authorizedFilterSection = [
  'Director',
  'General Manager',
  'Senior Manager',
  'Manager',
  'Supervisor'
];

const ChartTraining: React.FC<ChartTrainingProps> = ({
  title,
  endDate,
  startDate,
  buCode,
  buName,
  subBuCode,
  subBuName,
  deptCode,
  deptName,
  sectionCode,
  sectionName,
  bandName
}) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [openedDownload, { toggle: toggleDownload }] = useDisclosure(false);
  const [chartModal, setChartModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [chartFilter, setChartFilter] = useState({
    startDate: null,
    endDate: null,
    bulan: null,
    tahun: null,
    buName: null,
    subBuName: null,
    deptName: null,
    sectionName: null,
    buCode: null,
    subBuCode: null,
    deptCode: null,
    sectionCode: null
  });
  const [chartLevel, setChartLevel] = useState(0);
  const [currentData, setCurrentData] = useState(null);
  const [chartConfig, setChartConfig] = useState([]);
  const [statusFMSTR, setStatusFMSTR] = useState('0');
  const [userData, setUserData] = useLocalStorage('persist:root', null);
  const data = JSON.parse(userData?.auth);

  const maximumLevel = () => {
    if (bandName === 'Senior Manager') {
      return 3;
    }
    if (bandName === 'Manager') {
      return 2;
    }
    if (bandName === 'Supervisor') {
      return 3;
    }
    return 5;
  };

  const fetchedDataChart = useMutation({
    mutationFn: () =>
      getChartPlanCompletion({
        StartDate: chartFilter.startDate,
        EndDate: chartFilter.endDate,
        BinusianId: data?.userData?.binusianId,
        Bulan: chartFilter.bulan,
        Tahun: chartFilter.tahun,
        BuName: chartFilter.buName,
        SubBuName: chartFilter.subBuName,
        DeptName: chartFilter.deptName,
        SectionName: chartFilter.sectionName,
        BuCode: chartFilter.buCode,
        SubBuCode: chartFilter.subBuCode,
        DeptCode: chartFilter.deptCode,
        SectionCode: chartFilter.sectionCode
      }),
    onError: (error: any) => {
      showNotification({
        type: 'error',
        message: error
      });
    }
  });

  const fetchedDownloadChart = useMutation({
    mutationFn: () =>
      requestDownloadDataChartPlanTraining({
        StartDate: chartFilter.startDate,
        EndDate: chartFilter.endDate,
        Bulan: chartFilter.bulan,
        BinusianId: data?.userData?.binusianId,
        Tahun: chartFilter.tahun,
        BuName: chartFilter.buName,
        SubBuName: chartFilter.subBuName,
        Status: statusFMSTR,
        BuCode: chartFilter.buCode,
        SubBuCode: chartFilter.subBuCode,
        DeptCode: chartFilter.deptCode,
        SectionCode: chartFilter.sectionCode
      }),
    onSuccess: (data) => {
      if (data?.data?.data?.length > 0) {
        showNotification({
          type: 'success',
          message: 'Download in progress, please wait a moment.'
        });
        renderConvertExcel(data?.data?.data);
        return;
      }

      showNotification({
        type: 'error',
        message: `Data download is empty. Please try again later.`
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        message: error
      });
    }
  });

  const renderConvertExcel = (data: DataDownloadReportTraining[]) => {
    const formatFilename = () => {
      const parts = [
        `Training Plan vs Training Completion - download at ${dayjs().toLocaleString()}`
      ];
      return parts.filter(Boolean).join('_');
    };

    const filename = formatFilename();
    const wb = utils.book_new();

    // Convert the JSON data to a worksheet
    const ws = utils.json_to_sheet(data);

    // Add the worksheet to the workbook
    utils.book_append_sheet(wb, ws, 'Sheet1');

    // Write the workbook to a file
    // This will trigger a file download in the browser
    writeFile(wb, `${filename}.xlsx`);
  };

  useEffect(() => {
    setChartFilter((prevState) => ({
      ...prevState,
      startDate,
      endDate
    }));
  }, [
    endDate,
    startDate
  ]);

  useEffect(() => {
    if (chartFilter && chartFilter.startDate && chartFilter.endDate) {
      const commonFields = {
        startDate: chartFilter.startDate,
        endDate: chartFilter.endDate,
        bulan: chartFilter.bulan,
        tahun: chartFilter.tahun,
        buName: chartFilter.buName,
        buCode: chartFilter.buCode
      };

      const levelConfig = (chartLevel) => {
        const authorizedBu = authorizedFilterBu.includes(bandName);
        const authorizedSubBu = authorizedFilterSubBu.includes(bandName);
        const authorizedDept = authorizedFilterDept.includes(bandName);
        const authorizedSection = authorizedFilterSection.includes(bandName);
        let level = [
          {},
          {},
          {
            subBuName: chartFilter.subBuName,
            subBuCode: chartFilter.subBuCode
          },
          {
            subBuName: chartFilter.subBuName,
            subBuCode: chartFilter.subBuCode
          },
          {
            subBuName: chartFilter.subBuName,
            subBuCode: chartFilter.subBuCode,
            deptName: chartFilter.deptName,
            deptCode: chartFilter.deptCode
          },
          {
            subBuName: chartFilter.subBuName,
            subBuCode: chartFilter.subBuCode,
            deptName: chartFilter.deptName,
            deptCode: chartFilter.deptCode,
            sectionName: chartFilter.sectionName,
            sectionCode: chartFilter.sectionCode
          }
        ];
        if (
          !authorizedBu &&
          authorizedSubBu &&
          authorizedDept &&
          authorizedSection
        ) {
          level = [
            {},
            {},
            {
              subBuName: chartFilter.subBuName,
              subBuCode: chartFilter.subBuCode
            },
            {
              subBuName: chartFilter.subBuName,
              subBuCode: chartFilter.subBuCode,
              deptName: chartFilter.deptName,
              deptCode: chartFilter.deptCode
            },
            {
              subBuName: chartFilter.subBuName,
              subBuCode: chartFilter.subBuCode,
              deptName: chartFilter.deptName,
              deptCode: chartFilter.deptCode,
              sectionName: chartFilter.sectionName,
              sectionCode: chartFilter.sectionCode
            }
          ];
        }
        if (
          !authorizedBu &&
          !authorizedSubBu &&
          authorizedDept &&
          authorizedSection
        ) {
          level = [
            {},
            {},
            {
              deptName: chartFilter.deptName,
              deptCode: chartFilter.deptCode
            },
            {
              deptName: chartFilter.deptName,
              deptCode: chartFilter.deptCode,
              sectionName: chartFilter.sectionName,
              sectionCode: chartFilter.sectionCode
            }
          ];
        }
        if (
          !authorizedBu &&
          !authorizedSubBu &&
          !authorizedDept &&
          authorizedSection
        ) {
          level = [
            {},
            {},
            {
              sectionName: chartFilter.sectionName,
              sectionCode: chartFilter.sectionCode
            }
          ];
        }
        return level[chartLevel];
      };

      if (
        chartLevel >= 1 &&
        chartLevel <= maximumLevel() &&
        chartConfig.length === chartLevel - 1
      ) {
        setChartConfig((prevState) => [
          ...prevState,
          { ...commonFields, ...levelConfig(chartLevel) }
        ]);
      }
    }
  }, [chartFilter, chartLevel, chartConfig.length]);

  useEffect(() => {
    fetchedDataChart.mutate();
  }, [
    chartFilter?.buName,
    chartFilter?.bulan,
    chartFilter?.tahun,
    chartFilter?.subBuName,
    chartFilter?.startDate,
    chartFilter?.endDate,
    chartFilter?.deptName,
    chartFilter?.sectionName
  ]);

  const onClick = (element) => {
    
    if (element.length > 0) {
      if (chartLevel === 0) {
        setChartModal(true);
        if (
          bandName.toLowerCase() === 'director' ||
          bandName.toLowerCase() === 'general manager'
        ) {
          setChartLevel((prevLevel) => {
            return prevLevel + 1;
          });
        }
      }
      if (chartModal) {
        if (chartLevel < maximumLevel()) {
          setChartLevel((prevLevel) => {
            return prevLevel + 1;
          });
        }
        const index = element[0].index;
        // Get the label of the clicked bar
        const item = fetchedDataChart?.data?.data?.data[index];

        if (chartLevel < maximumLevel()) {
          
          setChartFilter((prevState) => ({
            ...prevState,
            bulan: item.month.toString(),
            tahun: item.year.toString(),
            buName:
              chartLevel === 1 ? item.label : prevState.buName || null,
            subBuName:
              chartLevel === 2 ? item.label : prevState.subBuName || null,
            deptName:
              chartLevel === 3 ? item.label : prevState.deptName || null,
            sectionName:
              chartLevel === 4 ? item.label : prevState.sectionName || null,
            buCode: 
              chartLevel === 1 ? item.code : prevState.buCode || null,
            subBuCode:
              chartLevel === 2 ? item.code : prevState.subBuCode || null,
            deptCode: 
              chartLevel === 3 ? item.code : prevState.deptCode || null,
            sectionCode:
              chartLevel === 4 ? item.code : prevState.sectionCode || null
          }));
        }
      }
    }
  };

  const handleCloseModal = () => {
    setChartFilter((prevState) => ({
      ...prevState,
      bulan: null,
      tahun: null,
      buName: null,
      subBuName: null,
      deptName: null,
      sectionName: null,
      buCode: null,
      subBuCode: null,
      deptCode: null,
      sectionCode: null
    }));

    setLoading(true);

    setTimeout(() => {
      setChartLevel(0);
      setChartConfig([]);
      setChartModal(false);
      setLoading(false);
      fetchedDataChart.mutate();
    }, 500);
  };

  useEffect(() => {
    setChartFilter((prevState) => ({
      ...prevState,
      bulan: chartConfig[chartLevel - 1]?.bulan,
      tahun: chartConfig[chartLevel - 1]?.tahun,
      buName: chartConfig[chartLevel - 1]?.buName,
      subBuName: chartConfig[chartLevel - 1]?.subBuName,
      buCode: chartConfig[chartLevel - 1]?.buCode,
      subBuCode: chartConfig[chartLevel - 1]?.subBuCode,
      deptCode: chartConfig[chartLevel - 1]?.deptCode,
      sectionCode: chartConfig[chartLevel - 1]?.sectionCode,
      deptName: chartConfig[chartLevel - 1]?.deptName,
      sectionName: chartConfig[chartLevel - 1]?.sectionName
    }));
    setCurrentData(chartConfig[chartLevel - 1]);
  }, [chartConfig, chartLevel]);

  const handleBack = () => {
    setLoading(true);
    if (chartLevel - 1 === 0) {
      setChartFilter((prevState) => ({
        ...prevState,
        bulan: null,
        tahun: null,
        buName: null,
        subBuName: null,
        buCode: null,
        subBuCode: null,
        deptName: null,
        deptCode: null,
        sectionName: null,
        sectionCode: null
      }));
      fetchedDataChart.mutate();
    }
    setTimeout(() => {
      setChartLevel((prevState) => prevState - 1);
      setChartConfig((prevState) => prevState.slice(0, -1));
      setLoading(false);
    }, 500);
  };

  const handleDownload = () => fetchedDownloadChart.mutate();

  const disabledClickChart = () => {
    if (bandName === 'Supervisor') {
      if (chartLevel === 1) {
        return false;
      }
      return true;
    } else {
      return true;
    }
  };

  const renderContent = () => {
    return (
      <BarChart
        key={chartLevel.toString()}
        chartKeys={['totalPlan', 'totalComplete']}
        chartLabels={['Training Plan', 'Training Complete']}
        chartColors={['#7B2183', '#75B843']}
        data={fetchedDataChart?.data?.data?.data || []}
        onClick={disabledClickChart() && onClick}
        loading={fetchedDataChart.isLoading}
        isError={fetchedDataChart.isError}
        onLoad={() => fetchedDataChart.mutate()}
        withLegend
      />
    );
  };

  const handleBreadcrumb = (item) => {
    setLoading(true);
    setTimeout(() => {
      setChartLevel(item.level);
      setChartConfig((prevState) => prevState.slice(0, item.level));
      setLoading(false);
    }, 500);
  };

  const renderModalBreadcrumb = () => {
    if (chartLevel <= 0) return null;
    if (bandName === 'Supervisor') return null;

    const breadcrumbData = [
      {
        title: currentData?.bulan
          ? dayjs()
              .month(currentData.bulan - 1)
              .locale(id)
              .format('MMMM')
          : '',
        level: 1
      },
      {
        title: currentData?.buName || '',
        level: 2
      },
      {
        title: currentData?.subBuName || '',
        level: 3
      },
      {
        title: currentData?.deptName || '',
        level: 4
      },
      {
        title: currentData?.sectionName || '',
        level: 5
      }
    ];

    const filteredItems = breadcrumbData.filter((item) => item.title);

    return (
      <Group gap={0}>
        {filteredItems.map((item, index) => (
          <React.Fragment key={index}>
            {/* <Button
              onClick={() => handleBreadcrumb(item)}
              variant="transparent"
            >
              {item.title}
            </Button> */}
            <Text className="text-sm-semibold">{item.title}</Text>
            {index !== filteredItems.length - 1 && <HiChevronRight />}
          </React.Fragment>
        ))}
      </Group>
    );
  };

  const onDownload = () => toggleDownload();

  const handleConfirmDownload = (data: any) => {
    setStatusFMSTR(data.status);
    setTimeout(() => {
      toggleDownload();
      handleDownload();
    }, 500);
  };

  return (
    <>
      <Grid.Col span={{ base: 12, md: 4 }}>
        {fetchedDataChart.isLoading && <LoaderChart />}
        {!fetchedDataChart.isLoading && (
          <Card h={'100%'}>
            <Flex gap={16} justify={'space-between'}>
              <Stack gap={0}>
                <Text className="text-sm-semibold">{title}</Text>
                <Text className="text-xs-medium" c={'gray'}>
                  Period{' '}
                  {dayjs(chartFilter.startDate)
                    .locale(id)
                    .format(dateFormat('dateyear'))}{' '}
                  -{' '}
                  {dayjs(chartFilter.endDate)
                    .locale(id)
                    .format(dateFormat('dateyear'))}
                </Text>
              </Stack>

              <Group>
                {fetchedDataChart.isError && (
                  <Button
                    variant="outline"
                    size="sm"
                    styles={{
                      padding: '6px 10px',
                      borderColor: '#CCCCCC',
                      color: '#333333',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12
                    }}
                    onClick={() => fetchedDataChart.mutate()}
                  >
                    <Unicons.UilRefresh size={14} />
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  styles={{
                    padding: '6px 10px',
                    borderColor: '#CCCCCC',
                    color: '#333333',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12
                  }}
                  onClick={() => open()}
                >
                  <Unicons.UilFilter size={14} />
                </Button>
              </Group>
            </Flex>
            <Flex mt={16}>{renderContent()}</Flex>
          </Card>
        )}
      </Grid.Col>
      <ChartFilter
        isVisible={opened}
        downloadable
        onDownload={onDownload}
        onClose={() => close()}
        loading={fetchedDownloadChart.isLoading}
        endDate={chartFilter.endDate}
        startDate={chartFilter.startDate}
        onFilter={(data) =>
          setChartFilter((prevState) => ({ ...prevState, ...data }))
        }
      />

      <ModalChartTraining
        title={title}
        bandName={bandName}
        description={renderModalBreadcrumb()}
        loading={loading}
        isVisible={chartModal}
        onClose={handleCloseModal}
        backButton={chartLevel > 0}
        onBack={handleBack}
        downloadable
        onDownload={onDownload}
        loadingDownload={fetchedDownloadChart.isLoading}
        chart={renderContent()}
      />

      <ModalDownload
        opened={openedDownload}
        title={`Download ${title}`}
        onClose={() => toggleDownload()}
        onConfirm={handleConfirmDownload}
      />
    </>
  );
};

export default ChartTraining;
